import Fuse, { type IFuseOptions } from "fuse.js";
import type { Partner } from "@/app/api/types/partner";
import type { SearchResult, SearchOptions } from "./types";
import { normalizeNumbersInQuery } from "./numberNormalization";
import { partnerSearchIndex } from "./searchIndex";

// Fuse.js configuration for partners
const PARTNER_FUSE_OPTIONS: IFuseOptions<Partner> = {
  keys: [
    { name: "name", weight: 2 },
    { name: "usage_name", weight: 1.5 },
    { name: "code", weight: 1 },
  ],
  threshold: 0.4,
  includeScore: true,
  minMatchCharLength: 1,
  ignoreLocation: true,
};

/**
 * Calculate match quality score for token-based matching
 * Higher score = better match
 *
 * Scoring breakdown:
 * - Base: 0.50 for all tokens found
 * - Exact word match bonus: +0.15 per token
 * - Prefix match bonus: +0.05 per token (for tokens >= 2 chars)
 * - Order bonus: +0.05 for tokens appearing in order
 */
function calculateTokenMatchScore(
  tokens: string[],
  text: string,
): { matched: boolean; score: number } {
  if (!text || tokens.length === 0) return { matched: false, score: 0 };

  const textLower = text.toLowerCase();
  const words = textLower.split(/\s+/);

  let matchedTokens = 0;
  let exactWordMatches = 0;
  let prefixMatches = 0;
  let orderBonus = 0;
  let lastMatchIndex = -1;

  for (const token of tokens) {
    const tokenLower = token.toLowerCase();

    // Check for exact word match first (highest priority)
    let wordIndex = words.findIndex((w) => w === tokenLower);
    if (wordIndex !== -1) {
      matchedTokens++;
      exactWordMatches++;
      if (wordIndex > lastMatchIndex) {
        orderBonus += 0.05;
      }
      lastMatchIndex = wordIndex;
      continue;
    }

    // For tokens >= 2 chars, allow prefix matching
    if (tokenLower.length >= 2) {
      wordIndex = words.findIndex((w) => w.startsWith(tokenLower));
      if (wordIndex !== -1) {
        matchedTokens++;
        prefixMatches++;
        if (wordIndex > lastMatchIndex) {
          orderBonus += 0.05;
        }
        lastMatchIndex = wordIndex;
        continue;
      }
    }

    // Fall back to substring match (lowest priority)
    if (textLower.includes(tokenLower)) {
      matchedTokens++;
      continue;
    }

    // Token not found at all
  }

  if (matchedTokens < tokens.length) {
    return { matched: false, score: 0 };
  }

  // Calculate score
  // Base: 0.50
  // Exact word matches: +0.15 per token (up to 0.30 max)
  // Prefix matches: +0.05 per token (up to 0.10 max)
  // Order bonus: up to 0.05
  const exactBonus = Math.min((exactWordMatches / tokens.length) * 0.3, 0.3);
  const prefixBonus = Math.min((prefixMatches / tokens.length) * 0.1, 0.1);
  const score = 0.5 + exactBonus + prefixBonus + Math.min(orderBonus, 0.05);

  return { matched: true, score: Math.min(score, 0.89) };
}

/**
 * Enhanced partner search with:
 * - Fuzzy matching with word boundary awareness
 * - Token order scoring
 * - Hybrid reordering (matches first, then non-matches)
 */
export function searchPartnersEnhanced(
  partners: Partner[],
  query: string,
  options: SearchOptions = {},
): SearchResult<Partner>[] {
  const {
    threshold = 0.4,
    includeNonMatching = true,
    normalizeNumbers = true,
  } = options;

  if (!query || query.trim() === "") {
    const index = partnerSearchIndex.getOrCreateIndex("", partners);
    const partnerMap = new Map(partners.map((p) => [p.id as string, p]));
    return index
      .map((id) => ({
        item: partnerMap.get(id as string)!,
        score: 0,
        matchType: "none" as const,
      }))
      .filter((r) => r.item);
  }

  const matchedIds = new Set<string>();
  const scoredResults: Array<{
    partner: Partner;
    score: number;
    matchType: "exact" | "starts-with" | "contains" | "fuzzy";
  }> = [];

  // Query variants for number normalization
  const queryVariants = normalizeNumbers
    ? normalizeNumbersInQuery(query)
    : [query];

  // TIER 1: Exact matches (score: 1.0)
  partners.forEach((partner) => {
    const nameLower = partner.name?.toLowerCase() || "";
    const usageNameLower = partner.usage_name?.toLowerCase() || "";
    const codeLower = partner.code?.toLowerCase() || "";

    for (const variant of queryVariants) {
      const v = variant.toLowerCase().trim();
      if (nameLower === v || usageNameLower === v || codeLower === v) {
        if (!matchedIds.has(partner.id)) {
          matchedIds.add(partner.id);
          scoredResults.push({ partner, score: 1.0, matchType: "exact" });
        }
        break;
      }
    }
  });

  // TIER 2: Starts-with matches (score: 0.95)
  partners.forEach((partner) => {
    if (matchedIds.has(partner.id)) return;

    const nameLower = partner.name?.toLowerCase() || "";
    const usageNameLower = partner.usage_name?.toLowerCase() || "";

    for (const variant of queryVariants) {
      const v = variant.toLowerCase().trim();
      if (nameLower.startsWith(v) || usageNameLower.startsWith(v)) {
        matchedIds.add(partner.id);
        scoredResults.push({ partner, score: 0.95, matchType: "starts-with" });
        break;
      }
    }
  });

  // TIER 3: Contains as substring (score: 0.90)
  partners.forEach((partner) => {
    if (matchedIds.has(partner.id)) return;

    const nameLower = partner.name?.toLowerCase() || "";
    const usageNameLower = partner.usage_name?.toLowerCase() || "";
    const codeLower = partner.code?.toLowerCase() || "";

    for (const variant of queryVariants) {
      const v = variant.toLowerCase().trim();
      if (
        nameLower.includes(v) ||
        usageNameLower.includes(v) ||
        codeLower.includes(v)
      ) {
        matchedIds.add(partner.id);
        scoredResults.push({ partner, score: 0.9, matchType: "contains" });
        break;
      }
    }
  });

  // TIER 4: Token-based matching with smart scoring (score: 0.60-0.89)
  partners.forEach((partner) => {
    if (matchedIds.has(partner.id)) return;

    const nameLower = partner.name?.toLowerCase() || "";
    const usageNameLower = partner.usage_name?.toLowerCase() || "";
    const codeLower = partner.code?.toLowerCase() || "";

    let bestScore = 0;

    for (const variant of queryVariants) {
      const v = variant.toLowerCase().trim();
      const tokens = v.split(/\s+/).filter((t) => t.length >= 1);

      if (tokens.length === 0) continue;

      // Try matching on each field and combined
      const nameResult = calculateTokenMatchScore(tokens, nameLower);
      const usageResult = calculateTokenMatchScore(tokens, usageNameLower);
      const combinedResult = calculateTokenMatchScore(
        tokens,
        `${nameLower} ${usageNameLower} ${codeLower}`,
      );

      // Take the best score from any field
      const fieldScore = Math.max(
        nameResult.matched ? nameResult.score + 0.05 : 0, // Bonus for name match
        usageResult.matched ? usageResult.score : 0,
        combinedResult.matched ? combinedResult.score : 0,
      );

      if (fieldScore > bestScore) {
        bestScore = fieldScore;
      }
    }

    if (bestScore > 0) {
      matchedIds.add(partner.id);
      scoredResults.push({ partner, score: bestScore, matchType: "contains" });
    }
  });

  // TIER 5: Fuzzy matching (score: 0.30-0.59)
  const unmatched = partners.filter((p) => !matchedIds.has(p.id));
  if (unmatched.length > 0) {
    const fuse = new Fuse(unmatched, { ...PARTNER_FUSE_OPTIONS, threshold });

    const fuseResults: Map<string, { item: Partner; score: number }> =
      new Map();
    queryVariants.forEach((variant) => {
      fuse.search(variant).forEach((result) => {
        const existing = fuseResults.get(result.item.id);
        if (
          !existing ||
          (result.score !== undefined && result.score < (existing.score ?? 1))
        ) {
          fuseResults.set(result.item.id, {
            item: result.item,
            score: result.score ?? 0,
          });
        }
      });
    });

    Array.from(fuseResults.values()).forEach(({ item, score }) => {
      matchedIds.add(item.id);
      // Convert Fuse score (0=perfect, 1=worst) to our score (1=best, 0=worst)
      // Map to 0.30-0.59 range for fuzzy matches
      const normalizedScore = 0.3 + (1 - score) * 0.29;
      scoredResults.push({
        partner: item,
        score: normalizedScore,
        matchType: "fuzzy",
      });
    });
  }

  // Sort by score descending
  scoredResults.sort((a, b) => b.score - a.score);

  // Build final results
  const results: SearchResult<Partner>[] = scoredResults.map(
    ({ partner, score, matchType }) => ({
      item: partner,
      score,
      matchType,
    }),
  );

  // TIER 6: Non-matching items (score: 0)
  if (includeNonMatching) {
    partners.forEach((partner) => {
      if (!matchedIds.has(partner.id)) {
        results.push({
          item: partner,
          score: 0,
          matchType: "none",
        });
      }
    });
  }

  // Cache index
  partnerSearchIndex.getOrCreateIndex(
    query,
    results.map((r) => r.item),
    () => results.map((r) => r.item),
  );

  return results;
}

/**
 * Track partner selection for frequency ordering
 */
export function trackPartnerSelection(partnerId: string): void {
  partnerSearchIndex.trackSelection(partnerId);
}
